// plugins/applicationinsights.ts
import { env } from '@/utilities/env';
import { ApplicationInsights, type IPageViewTelemetry, type ITelemetryItem } from '@microsoft/applicationinsights-web';

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

const config = {
    connectionString: env('APPLICATIONINSIGHTS_CONNECTION_STRING'),
    instrumentationKey: env('APPINSIGHTS_INSTRUMENTATIONKEY'),
    enableCorsCorrelation: isProduction,
    enableRequestHeaderTracking: isDevelopment,
    enableResponseHeaderTracking: isDevelopment,
    disableAjaxTracking: false,
    disableFetchTracking: false,
    samplingPercentage: isDevelopment ? 100 : 20,
    isCookieUseDisabled: !isProduction
};

// Sensitive data lists
const SENSITIVE_HEADERS = ['authorization', 'x-api-key', 'cookie', 'x-auth-token'] as const;
const SENSITIVE_URL_PARAMS = ['token', 'apikey', 'password', 'secret', 'ssn', 'email'] as const;

// Privacy filter function
const createPrivacyFilter = () => {
    return (envelope: ITelemetryItem): boolean => {
        if (!isProduction) return true;
        try {
            if (envelope.data?.baseData && typeof envelope.data.baseData === 'object') {
                const baseData = envelope.data.baseData;

                if (baseData.headers && typeof baseData.headers === 'object') {
                    SENSITIVE_HEADERS.forEach(header => {
                        delete baseData.headers[header];
                    });
                }

                if (baseData.url && typeof baseData.url === 'string') {
                    baseData.url = sanitizeUrl(baseData.url);
                }

                if (baseData.properties && typeof baseData.properties === 'object') {
                    delete baseData.properties.userAgent;
                    delete baseData.properties.ipAddress;
                }
            }
        } catch (error) {
            console.warn('Error in privacy filter:', error);
        }

        return true;
    };
};

const sanitizeUrl = (urlString: string): string => {
    try {
        const url = new URL(urlString);

        SENSITIVE_URL_PARAMS.forEach(param => {
            if (url.searchParams.has(param)) {
                url.searchParams.set(param, '[REDACTED]');
            }
        });

        return url.toString();
    } catch (error) {
        console.warn('Failed to sanitize URL:', urlString, error);
        return urlString;
    }
};

let appInsights: ApplicationInsights | null = null;

const initializeAppInsights = (): ApplicationInsights | null => {
    // Don't initialize if no connection string
    if (!env('APPLICATIONINSIGHTS_CONNECTION_STRING') || !env('APPINSIGHTS_INSTRUMENTATIONKEY')) {
        console.log('Application Insights disabled: no connection string');
        return null;
    }

    try {
        appInsights = new ApplicationInsights({ config });
        appInsights.loadAppInsights();
        appInsights.addTelemetryInitializer(createPrivacyFilter());

        console.log(`Application Insights initialized for ${process.env.NODE_ENV} environment`);
        return appInsights;
    } catch (error) {
        console.error('Failed to initialize Application Insights:', error);
        return null;
    }
};

const instance = initializeAppInsights();

export const appInsightsService = {
    trackPageView: (data?: IPageViewTelemetry) => instance?.trackPageView(data),
    flush: () => instance?.flush(),
    setUserId: (userId: string) => instance?.setAuthenticatedUserContext(userId),
    clearUserId: () => instance?.clearAuthenticatedUserContext(),
    isInitialized: () => instance !== null
};

export default instance;